import { Environment, subdomainEnvironment } from './environments';

const getLaunchDarklyClientSideId = (environment: Environment): string => {
  switch (environment) {
    case 'prod':
      return '652f17e244cb56124fa8cf96';
    case 'dev':
      return '65c66efbfc74a80ff1860f04';
    default:
      return '652f17e244cb56124fa8cf95';
  }
};

// hire-go is a single multi-tenant app, so its origin is keyed off the
// environment (not the per-tenant domain). Only `prod` points at the live
// app; every non-prod environment shares the staging deployment. A non-empty
// REACT_APP_HIRE_GO_BASE_URL overrides this, but the build bakes it in at build
// time, so it applies to every host that bundle serves.
export const getHireGoOrigin = (environment: Environment): string =>
  environment === 'prod'
    ? 'https://go.fountain.com'
    : 'https://staging.tryfountain.com';

// Matches an RWX sandbox host in the fountain org. RWX serves one task under
// two hostname shapes: recruiter-ui-alice--fountain.rwx.run (friendly) and
// 10228fac…--fountain.rwx.run (canonical, pinned to the task cache key).
// The region segment is optional so the legacy
// recruiter-ui-alice--fountain.r1.rwx.run spelling still matches. `rwx.run` is a
// shared multi-tenant domain, so the `--fountain` org segment is required.
const FOUNTAIN_RWX_SANDBOX_MATCHER = /--fountain\.(?:[a-z0-9-]+\.)?rwx\.run$/;

/**
 * `'include'` for a Fountain RWX preview host over https, otherwise undefined,
 * the fetch default. A private preview admits a browser request only with
 * that host's RWX access cookie, so a cross-host call to one sends
 * credentials. Production hosts never match.
 */
export const rwxPreviewCredentials = (
  url: string,
): RequestCredentials | undefined => {
  try {
    const { protocol, hostname } = new URL(url, window.location.href);
    return protocol === 'https:' && FOUNTAIN_RWX_SANDBOX_MATCHER.test(hostname)
      ? 'include'
      : undefined;
  } catch {
    return undefined;
  }
};

export interface DomainMeta {
  environment: Environment;
  monolithOrigin: string;
  tenant: string;
  launchDarklyClientSideId: string;
}

export const inferDomainMeta = (hostname: string): DomainMeta => {
  if (hostname === 'app.fountain.com') {
    return {
      environment: 'prod',
      monolithOrigin: 'https://web.fountain.com',
      tenant: 'fountain',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('prod'),
    };
  }

  if (hostname === 'app.uat-01.fountain.com') {
    return {
      environment: 'stage',
      monolithOrigin: 'https://uat-01.fountain.com',
      tenant: 'uat-01',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('stage'),
    };
  }

  if (
    hostname.startsWith('app.') &&
    (hostname.endsWith('.fountain.com') ||
      hostname.endsWith('.tryfountain.com'))
  ) {
    const environment = subdomainEnvironment(hostname);
    return {
      environment,
      monolithOrigin: `https://${hostname.slice(4)}`,
      tenant: hostname.slice(4).split('.')[0],
      launchDarklyClientSideId: getLaunchDarklyClientSideId(environment),
    };
  }

  if (hostname.endsWith('.chromatic.com')) {
    return {
      environment: 'stage',
      monolithOrigin: 'http://localhost:3000',
      tenant: 'storybook',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('stage'),
    };
  }

  if (hostname === 'localhost' && process.env.DEV_SERVER_URL) {
    return {
      environment: 'dev',
      monolithOrigin: process.env.DEV_SERVER_URL,
      tenant: 'local',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  if (hostname === 'localhost' && process.env.NODE_ENV === 'development') {
    return {
      environment: 'dev',
      monolithOrigin: 'http://localhost:3000',
      tenant: 'local',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  // TODO: Merge the previous and following if statements and use ENV variables.
  if (hostname === 'localhost' && process.env.NODE_ENV === 'test') {
    return {
      environment: 'dev',
      monolithOrigin: 'http://localhost:3023',
      tenant: 'local',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  if (
    FOUNTAIN_RWX_SANDBOX_MATCHER.test(hostname) &&
    /^(?:recruiter-ui|applicant-ui)-/.test(hostname)
  ) {
    // RWX dev sandbox, friendly hostname:
    // recruiter-ui-{slug}-- or applicant-ui-{slug}-- → hire-{slug}--
    //
    // The canonical hostname carries an opaque hash instead of the prefix, so
    // no swap can derive a monolith host from it. It falls through to the
    // REACT_APP_MONOLITH_BASE_URL branch at the end of this function.
    const monolithHostname = hostname.replace(
      /^(?:recruiter-ui|applicant-ui)-/,
      'hire-',
    );
    return {
      environment: 'dev',
      monolithOrigin: `https://${monolithHostname}`,
      tenant: 'rwx-sandbox',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  if (hostname.endsWith('.modal.host')) {
    // Open-Inspect / Modal sandbox tunnel (e.g. ta-<id>-8991-<rand>.w.modal.host). recruiter-ui is
    // served through an nginx reverse proxy that routes /api_self_serve, /users, /oauth and /front
    // to the monolith on the SAME origin, so use a same-origin (empty) monolith origin.
    return {
      environment: 'dev',
      monolithOrigin: '',
      tenant: 'modal-sandbox',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  // Last resort for a host this function cannot map by shape. Only a sandbox
  // build bakes REACT_APP_MONOLITH_BASE_URL in (.rwx/hire-webapps.yml), so a
  // production build still throws below. This covers RWX's canonical,
  // task-cache-key-pinned hostname — visual QA drives those to pin the exact
  // commit a run built — whose opaque hash carries no slug to swap, and it
  // keeps the SPA rendering if RWX changes its hostname shape again.
  if (process.env.REACT_APP_MONOLITH_BASE_URL) {
    return {
      environment: 'dev',
      monolithOrigin: process.env.REACT_APP_MONOLITH_BASE_URL,
      tenant: 'rwx-sandbox',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  throw new Error('Unable to map hostname to a known domain');
};

export const domainMeta = inferDomainMeta(window.location.hostname);
